import { createSelector } from 'reselect';
import getAllCategoryInfos from './getAllCategoryInfos';
import getMenu from './getMenu';
import getItems from './getItems';
import getNestedItemStockBalancesData from './getNestedItemStockBalancesData';
import {
  getChoiceSetDiscountConfig,
  getUpsellDisplayLimit,
  getUpsellUnavailableDisplayType,
} from './config';
import {
  checkTimeAvailability,
  itemCategoryAvailibilityValidate,
  validateUpsellItems,
} from '../utils/ordering';
import { UPSELL_ITEM_DISPLAY_TYPE } from '../constants';
import getOrderDateTimeSelected from './getOrderDateTimeSelected';
import processUpsellNameOverride from '../utils/processors/processUpsellNameOverride';
import { filterAlcoholicItemsFromChoiceSet } from '../utils/ordering/filterAlcoholic';
import getUserUnderage from './getUserUnderage';

export default createSelector(
  [
    getMenu,
    getItems,
    getUpsellDisplayLimit,
    getNestedItemStockBalancesData(undefined),
    getUpsellUnavailableDisplayType,
    getAllCategoryInfos,
    getChoiceSetDiscountConfig,
    getOrderDateTimeSelected,
    getUserUnderage,
  ],
  (
    orderMenu,
    items,
    upsellDisplayLimit = 3,
    [stockBalanceDataMap],
    unavailableItemDisplay,
    categories,
    choiceSetDiscountConfig,
    dateTimeSelected,
    isUserUnderage,
  ): (Item & { upsellAvailable: boolean })[] => {
    if (!orderMenu) {
      return [];
    }
    const {
      upsells,
      rootMenuNode: { subMenus },
    } = orderMenu;

    if (!items || !upsells) return [];

    let filteredUpsells = (upsells.filter(u =>
      checkTimeAvailability({
        availability: u.availability,
        days: u.days as Day[],
        start: u.start,
        end: u.end,
        dateTimeSelected: dateTimeSelected,
      }),
    ) || [])[0];

    if (isUserUnderage) {
      filteredUpsells = filterAlcoholicItemsFromChoiceSet(
        filteredUpsells,
      ) as RedCat.UpsellChoiceSet;
    }

    if (!filteredUpsells) return [];

    const upsellItems = filteredUpsells.choices
      .filter(choice => items[choice.id]) // Filter out choices not in items to prevent errors in validateUpsellItems
      .map(choice => processUpsellNameOverride({ choice, items }))
      .filter(
        c =>
          c &&
          itemCategoryAvailibilityValidate({
            id: c.id,
            subMenus: subMenus,
            categoriesInfo: categories || [],
            dateTimeSelected,
          }),
      );
    const validatedUpSellItems = validateUpsellItems(
      upsellItems,
      stockBalanceDataMap,
      choiceSetDiscountConfig,
      dateTimeSelected,
    );

    const result =
      unavailableItemDisplay === UPSELL_ITEM_DISPLAY_TYPE.GREYOUT
        ? validatedUpSellItems
        : validatedUpSellItems?.filter(item => item.upsellAvailable);
    return (result || []).slice(0, upsellDisplayLimit);
  },
);
